'use client';

import type { TodoTask } from '@prisma/client';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, Check, Edit, Flag, Loader2, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useOptimistic, useState } from 'react';
import toast from 'react-hot-toast';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { cn } from '../../../lib/utils';
import { updateTask, updateTaskCompletion, updateTaskSchedule } from '../lib/actions/todoActions';

import { DatePicker } from './DatePicker';
import { ReminderPicker } from './ReminderPicker';

const priorityMap: Record<number, { label: string; icon: string }> = {
  1: { label: 'Срочно', icon: 'text-red-500' },
  2: { label: 'Высокий', icon: 'text-orange-500' },
  3: { label: 'Средний', icon: 'text-sky-500' },
  4: { label: 'Низкий', icon: 'text-slate-500' },
};

interface TaskDetailsDialogProps {
  task: TodoTask;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: () => void;
  onUpdate: (updatedTask: TodoTask) => void;
  startEditing?: boolean;
  onStartEditingChange?: (v: boolean) => void;
}

export function TaskDetailsDialog({
  task,
  open,
  onOpenChange,
  onDelete,
  onUpdate,
  startEditing = false,
  onStartEditingChange,
}: TaskDetailsDialogProps) {
  const [optimisticTask, setOptimisticTask] = useOptimistic(
    task,
    (state: TodoTask, payload: Partial<TodoTask> | boolean) => {
      if (typeof payload === 'boolean') return { ...state, isCompleted: payload };
      return { ...state, ...payload };
    },
  );

  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(task.content);
  const [description, setDescription] = useState(task.description ?? '');

  const [isSavingText, setIsSavingText] = useState(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [isSavingCompletion, setIsSavingCompletion] = useState(false);

  const dueDate = useMemo(
    () => (optimisticTask.dueDate ? new Date(optimisticTask.dueDate) : undefined),
    [optimisticTask.dueDate],
  );
  const reminderAt = useMemo(
    () => (optimisticTask.reminderAt ? new Date(optimisticTask.reminderAt) : undefined),
    [optimisticTask.reminderAt],
  );

  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      return;
    }

    // когда открываем — синхронизируем поля ввода
    setContent(task.content);
    setDescription(task.description ?? '');

    if (startEditing) {
      setIsEditing(true);
      onStartEditingChange?.(false);
    }
  }, [open, startEditing, task.content, task.description, onStartEditingChange]);

  const handleToggleComplete = useCallback(async () => {
    if (isSavingCompletion) return;

    const next = !optimisticTask.isCompleted;
    setIsSavingCompletion(true);
    setOptimisticTask(next);

    try {
      const updated = await updateTaskCompletion(optimisticTask.id, next);
      onUpdate(updated);
      toast.success(next ? 'Задача выполнена!' : 'Задача возвращена в работу');
    } catch {
      setOptimisticTask(!next);
      toast.error('Не удалось обновить статус.');
    } finally {
      setIsSavingCompletion(false);
    }
  }, [isSavingCompletion, optimisticTask, onUpdate, setOptimisticTask]);

  const persistSchedule = useCallback(
    async (
      patch: { dueDate?: Date | null; reminderAt?: Date | null },
      optimisticPatch: Partial<TodoTask>,
    ) => {
      if (isSavingSchedule) return;

      setIsSavingSchedule(true);
      setOptimisticTask(optimisticPatch);

      try {
        const updated = await updateTaskSchedule(optimisticTask.id, patch);
        onUpdate(updated);
      } catch {
        toast.error('Не удалось сохранить дату/напоминание.');
      } finally {
        setIsSavingSchedule(false);
      }
    },
    [isSavingSchedule, optimisticTask.id, onUpdate, setOptimisticTask],
  );

  const handleDueDateChange = useCallback(
    (d?: Date) => {
      // если срок убрали — напоминание тоже убираем
      if (!d) {
        void persistSchedule(
          { dueDate: null, reminderAt: null },
          { dueDate: null, reminderAt: null },
        );
        return;
      }
  
      // ✅ Оптимистично сдвигаем reminderAt, если он был пресетом
      const PRESETS_MS = [30 * 60 * 1000, 60 * 60 * 1000, 24 * 60 * 60 * 1000];
      const TOLERANCE_MS = 60_000;
  
      const prevDue = dueDate ? new Date(dueDate) : null;
      const prevRem = reminderAt ? new Date(reminderAt) : null;
  
      let optimisticPatch: Partial<TodoTask> = { dueDate: d };
  
      if (prevDue && prevRem) {
        const diff = prevDue.getTime() - prevRem.getTime();
        const offset = PRESETS_MS.find((ms) => Math.abs(diff - ms) <= TOLERANCE_MS) ?? null;
  
        if (offset != null) {
          optimisticPatch = {
            ...optimisticPatch,
            reminderAt: new Date(d.getTime() - offset),
          };
        }
      }
  
      void persistSchedule({ dueDate: d }, optimisticPatch);
    },
    [dueDate, reminderAt, persistSchedule],
  );
  

  const handleReminderChange = useCallback(
    (d?: Date) => {
      // ReminderPicker сам disabled без dueDate, но на всякий
      if (!optimisticTask.dueDate) {
        toast.error('Сначала выберите срок выполнения задачи');
        return;
      }

      void persistSchedule({ reminderAt: d ?? null }, { reminderAt: d ?? null });
    },
    [optimisticTask.dueDate, persistSchedule],
  );

  const handleSaveText = useCallback(async () => {
    if (isSavingText) return;

    const nextContent = content.trim();
    if (!nextContent) {
      toast.error('Название не может быть пустым');
      return;
    }

    const trimmedDesc = description.trim();
    const nextDescription: string | null = trimmedDesc ? trimmedDesc : null;

    const hasChanges =
      nextContent !== (task.content ?? '') || nextDescription !== (task.description ?? null);

    if (!hasChanges) {
      setIsEditing(false);
      return;
    }

    setIsSavingText(true);

    try {
      const updated = await updateTask(optimisticTask.id, {
        content: nextContent,
        description: nextDescription,
      });

      setOptimisticTask({ content: updated.content, description: updated.description });
      onUpdate(updated);
      toast.success('Задача обновлена!');
      setIsEditing(false);
    } catch {
      toast.error('Не удалось сохранить изменения.');
    } finally {
      setIsSavingText(false);
    }
  }, [
    isSavingText,
    content,
    description,
    task.content,
    task.description,
    optimisticTask.id,
    onUpdate,
    setOptimisticTask,
  ]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setContent(task.content);
    setDescription(task.description ?? '');
  }, [task.content, task.description]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-slate-800 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle className="sr-only">Детали задачи</DialogTitle>

          <div className="flex items-start justify-between gap-4 w-full">
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <Input
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="bg-slate-700/80 text-xl font-bold border-slate-600 text-white"
                />
              ) : (
                <h2 className="text-xl font-bold break-words">{optimisticTask.content}</h2>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-400">
                <div className="flex items-center gap-1.5">
                  <Flag
                    className={cn(
                      'w-4 h-4',
                      (priorityMap[optimisticTask.priority] ?? priorityMap[4]).icon,
                    )}
                  />
                  <span>{(priorityMap[optimisticTask.priority] ?? priorityMap[4]).label}</span>
                </div>

                {optimisticTask.dueDate && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-sky-400" />
                    <span>
                      {format(new Date(optimisticTask.dueDate), 'dd MMMM yyyy', { locale: ru })}
                    </span>
                  </div>
                )}
              </div>

              {/* ✅ СРОК + НАПОМИНАНИЕ (сохраняем сразу в БД) */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <DatePicker date={dueDate} onDateChange={handleDueDateChange} />
                <ReminderPicker
                  dueDate={dueDate}
                  reminderDate={reminderAt}
                  onReminderSet={handleReminderChange}
                  triggerVariant="button"
                  showBadge
                />

                {isSavingSchedule && (
                  <span className="text-xs text-slate-400 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Сохраняю…
                  </span>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-4">
          <span className="text-xs text-slate-400 uppercase tracking-wide">Описание</span>

          <AnimatePresence mode="wait">
            {!isEditing ? (
              <motion.div
                key="view"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <p className="mt-1 text-base text-slate-300 whitespace-pre-wrap break-words min-h-[100px]">
                  {optimisticTask.description || (
                    <span className="italic text-slate-500">Нет описания</span>
                  )}
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="edit"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                  placeholder="Добавьте полезные детали..."
                  className="mt-1 w-full bg-slate-700/80 border-slate-600 text-slate-200"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:justify-between items-center gap-3">
          <Button
            type="button"
            onClick={handleToggleComplete}
            disabled={isSavingCompletion}
            className={cn(
              'w-full sm:w-auto flex items-center gap-2 transition',
              optimisticTask.isCompleted
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : 'bg-slate-700 hover:bg-slate-600 text-slate-200',
            )}
          >
            <Check size={16} />
            {optimisticTask.isCompleted ? 'Выполнено' : 'Отметить как выполненную'}
          </Button>

          <div className="w-full sm:w-auto flex items-center justify-end gap-2">
            {isEditing ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancelEdit}
                  className="text-slate-400 hover:text-white"
                >
                  Отмена
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveText}
                  className="bg-sky-600 hover:bg-sky-500 text-white"
                  disabled={isSavingText}
                >
                  {isSavingText ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Сохранить'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsEditing(true)}
                  className="hover:bg-slate-700"
                >
                  <Edit className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Редактировать</span>
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="destructive" className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Удалить</span>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Удалить задачу?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Действие нельзя будет отменить. Задача «{optimisticTask.content}» будет
                        удалена навсегда.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Отмена</AlertDialogCancel>
                      <AlertDialogAction onClick={onDelete}>Удалить</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
